import React, { useEffect, useState, useCallback } from 'react';
import { 
  Users, Truck, CheckSquare, AlertTriangle, TrendingUp, 
  AlertCircle, Bell, Clock, Calendar, ChevronRight, 
  Plus, Zap, Activity, Target
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { formatDateTime, formatDate, getPriorityColor } from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { useSmartRefresh, WS_EVENTS } from '../hooks/useSmartRefresh';

// Widget Component
const Widget = ({ children, className = '', size = 'sm', onClick, testId }) => {
  return (
    <div 
      onClick={onClick}
      className={`widget ${size === 'lg' ? 'widget-lg' : size === 'md' ? 'widget-md' : 'widget-sm'} ${onClick ? 'cursor-pointer active:scale-[0.98]' : ''} ${className}`}
      data-testid={testId}
    >
      {children}
    </div>
  );
};

// Stat Widget Component - Sans flèche, toujours cliquable
const StatWidget = ({ title, value, icon: Icon, color, link, delay = 0 }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  return (
    <Widget 
      onClick={() => navigate(link)} 
      testId={`stat-widget-${title.toLowerCase().replace(/\s/g, '-')}`}
      className={`animate-slide-up stagger-${delay + 1}`}
    >
      <div className="flex flex-col items-center justify-center h-full text-center p-2">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color} mb-3`}>
          <Icon size={22} className="text-white" />
        </div>
        <p className={cn("stat-value", theme === 'dark' ? 'text-white' : 'text-gray-900')}>{value}</p>
        <p className={cn("stat-label mt-1 text-center", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>{title}</p>
      </div>
    </Widget>
  );
};

// Alert Task Widget
const AlertTaskWidget = ({ tasks, onUpdateStatus }) => {
  const navigate = useNavigate();
  
  if (!tasks || tasks.length === 0) return null;

  return (
    <Widget size="lg" className="animate-slide-up stagger-5 !bg-gradient-to-br from-red-900/30 to-orange-900/20 border-red-500/30">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
            <Bell size={16} className="text-red-400 animate-pulse" />
          </div>
          <h3 className="text-sm font-bold text-white uppercase tracking-wide">
            Alertes ({tasks.length})
          </h3>
        </div>
        <button 
          onClick={() => navigate('/tasks')}
          className="text-xs text-red-400 hover:text-red-300 transition-colors"
        >
          Tout voir
        </button>
      </div>
      
      <div className="space-y-2 max-h-[100px] overflow-y-auto">
        {tasks.slice(0, 3).map((task) => (
          <div
            key={task.id}
            className="flex items-center justify-between p-2 rounded-lg bg-black/30 border border-white/5"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{task.titre}</p>
              <p className="text-xs text-zinc-400 flex items-center gap-1">
                <Clock size={10} />
                {task.date_echeance ? formatDate(task.date_echeance) : 'Sans échéance'}
              </p>
            </div>
            <Button
              size="sm"
              className="ml-2 h-7 px-2 text-xs bg-red-500 hover:bg-red-600"
              onClick={(e) => {
                e.stopPropagation();
                onUpdateStatus(task.id, task.statut === 'en_attente' ? 'en_cours' : 'terminee');
              }}
            >
              {task.statut === 'en_attente' ? 'Démarrer' : 'Terminer'}
            </Button>
          </div>
        ))}
      </div>
    </Widget>
  );
};

// Quick Actions Widget
const QuickActionsWidget = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  const actions = [
    { label: 'Planning', icon: Calendar, path: '/planning', color: 'bg-cyan-500/20 text-cyan-500' },
    { label: 'Nouvelle tâche', icon: Plus, path: '/tasks', color: 'bg-green-500/20 text-green-500' },
    { label: 'Incidents', icon: AlertTriangle, path: '/incidents', color: 'bg-amber-500/20 text-amber-500' },
  ];

  return (
    <Widget size="md" className="animate-slide-up stagger-4">
      <h3 className={cn("text-xs font-bold uppercase tracking-wider mb-3", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
        Actions rapides
      </h3>
      <div className="flex gap-2">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.path}
              onClick={() => navigate(action.path)}
              className={`flex-1 flex flex-col items-center justify-center p-3 rounded-xl ${action.color} bg-opacity-10 hover:bg-opacity-20 transition-all active:scale-95`}
            >
              <Icon size={20} />
              <span className={cn("text-[10px] mt-1 font-medium", theme === 'dark' ? 'text-white/90' : 'text-gray-700')}>{action.label}</span>
            </button>
          );
        })}
      </div>
    </Widget>
  );
};

// Fleet Status Widget
const FleetStatusWidget = ({ vehiclesOk, vehiclesMaintenance, total }) => {
  const percentage = total > 0 ? Math.round((vehiclesOk / total) * 100) : 0;
  const { theme } = useTheme();
  
  return (
    <Widget size="md" className="animate-slide-up stagger-3">
      <div className="flex items-center justify-between mb-3">
        <h3 className={cn("text-xs font-bold uppercase tracking-wider", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
          Flotte
        </h3>
        <span className={cn("text-2xl font-bold", theme === 'dark' ? 'text-white' : 'text-gray-900')}>{percentage}%</span>
      </div>
      <div className="flex gap-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500 shadow-[0_0_10px_rgba(0,230,118,0.5)]" />
          <span className={cn("text-xs", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>{vehiclesOk} OK</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500 shadow-[0_0_10px_rgba(255,234,0,0.5)]" />
          <span className={cn("text-xs", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>{vehiclesMaintenance} Maint.</span>
        </div>
      </div>
      {/* Progress Bar */}
      <div className={cn("mt-3 h-2 rounded-full overflow-hidden", theme === 'dark' ? 'bg-zinc-800' : 'bg-gray-200')}>
        <div 
          className="h-full bg-gradient-to-r from-green-500 to-cyan-500 rounded-full transition-all duration-1000"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </Widget>
  );
};

// Reminder Widget Component
const ReminderWidget = ({ reminders }) => {
  if (!reminders || reminders.length === 0) return null;

  return (
    <Widget size="md" className="animate-slide-up stagger-5">
      <div className="flex items-center gap-2 mb-3">
        <Zap size={16} className="text-cyan-400" />
        <h3 className="text-xs font-bold text-zinc-400 uppercase tracking-wider">
          Rappels
        </h3>
      </div>
      <div className="space-y-2">
        {reminders.slice(0, 2).map((reminder, idx) => (
          <div key={idx} className="reminder-card">
            <p className="text-sm font-medium text-white">{reminder.titre}</p>
            <p className="text-xs text-zinc-400">{reminder.message}</p>
          </div>
        ))}
      </div>
    </Widget>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [alertTasks, setAlertTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await api.get('/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAlertTasks = useCallback(async () => {
    try {
      const response = await api.get('/tasks/alerts');
      setAlertTasks(response.data || []);
    } catch (error) {
      console.error('Error fetching alert tasks:', error);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchDashboard(), fetchAlertTasks()]);
  }, [fetchDashboard, fetchAlertTasks]);

  // Initial fetch
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Smart refresh: WebSocket pour temps réel, polling fallback 30s
  useSmartRefresh(fetchAll, {
    wsEvents: [
      WS_EVENTS.VEHICLE_UPDATED, WS_EVENTS.VEHICLE_CREATED, WS_EVENTS.VEHICLE_DELETED,
      WS_EVENTS.EMPLOYEE_UPDATED, WS_EVENTS.EMPLOYEE_CREATED, WS_EVENTS.EMPLOYEE_DELETED,
      WS_EVENTS.TASK_UPDATED, WS_EVENTS.TASK_CREATED,
      WS_EVENTS.INCIDENT_CREATED, WS_EVENTS.INCIDENT_UPDATED,
      WS_EVENTS.DASHBOARD_UPDATE
    ],
    fallbackInterval: 30000, // 30 secondes au lieu de 1 seconde
    refreshOnMount: false // Déjà fait au-dessus
  });

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}`, { statut: newStatus });
      toast.success('Tâche mise à jour');
      fetchAlertTasks();
      fetchDashboard();
    } catch (error) {
      const errorMsg = error?.response?.data?.detail || 'Erreur lors de la mise à jour';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur de mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="dashboard-page">
      {/* Welcome Header - Mobile */}
      <div className="px-4 pt-4 md:px-0 md:pt-0">
        <p className={cn("text-sm", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Bonjour,</p>
        <h1 className={cn("text-2xl font-bold uppercase tracking-wide", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
          {user?.prenom || 'Utilisateur'}
        </h1>
      </div>

      {/* Bento Grid */}
      <div className="bento-grid">
        {/* Stat Widgets */}
        <StatWidget
          title="Salariés"
          value={stats?.total_employees || 0}
          icon={Users}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          link="/employees"
          delay={0}
        />
        
        <StatWidget
          title="Véhicules"
          value={stats?.total_vehicles || 0}
          icon={Truck}
          color="bg-gradient-to-br from-green-500 to-green-600"
          link="/vehicles"
          delay={1}
        />
        
        <StatWidget
          title="Tâches"
          value={stats?.active_tasks || 0}
          icon={CheckSquare}
          color="bg-gradient-to-br from-amber-500 to-amber-600"
          link="/tasks"
          delay={2}
        />
        
        <StatWidget
          title="Incidents"
          value={stats?.open_incidents || 0}
          icon={AlertTriangle}
          color="bg-gradient-to-br from-red-500 to-red-600"
          link="/incidents"
          delay={3}
        />

        {/* Fleet Status Widget */}
        <FleetStatusWidget 
          vehiclesOk={stats?.vehicles_ok || 0}
          vehiclesMaintenance={stats?.vehicles_maintenance || 0}
          total={stats?.total_vehicles || 0}
        />

        {/* Quick Actions Widget */}
        <QuickActionsWidget />

        {/* Alert Tasks Widget */}
        {alertTasks.length > 0 && (
          <AlertTaskWidget 
            tasks={alertTasks} 
            onUpdateStatus={handleUpdateTaskStatus}
          />
        )}

        {/* Reminders Widget */}
        {stats?.alerts && stats.alerts.length > 0 && (
          <ReminderWidget reminders={stats.alerts} />
        )}
      </div>

      {/* Desktop: Full Alerts Section */}
      <div className="hidden md:block px-4 md:px-0">
        {stats?.alerts && stats.alerts.length > 0 && (
          <div className={cn(
            "rounded-xl p-6 animate-slide-up border",
            theme === 'dark' ? 'bg-black border-white/10' : 'bg-white border-gray-200 shadow-sm'
          )}>
            <h3 className={cn("text-lg font-bold uppercase tracking-wide mb-4 flex items-center gap-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              <AlertCircle className="text-red-500" />
              Alertes importantes
            </h3>
            
            <div className="space-y-3">
              {stats.alerts.map((alert, index) => (
                <div
                  key={index}
                  className={cn(
                    "p-3 rounded-lg border",
                    theme === 'dark' ? 'bg-zinc-900/50 border-white/5' : 'bg-gray-50 border-gray-100'
                  )}
                  data-testid={`alert-${index}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className={cn("font-medium text-sm truncate", theme === 'dark' ? 'text-white' : 'text-gray-900')}>{alert.titre}</p>
                      <p className={cn("text-xs mt-0.5", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>{formatDateTime(alert.created_at)}</p>
                    </div>
                    {alert.link && (
                      <button
                        onClick={() => navigate(alert.link)}
                        className="text-sm font-medium text-red-500 hover:text-red-400 transition-colors flex-shrink-0"
                      >
                        Voir
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
